import type { TotalByRiskLevelWithPercentage } from '@/domains/home/home.entity';

/**
 * Interfaz para los totales agregados de niveles de riesgo
 */
interface RiskTotals {
  riesgo_alto: number;
  riesgo_medio: number;
  riesgo_bajo: number;
}

/**
 * Calcula el porcentaje de un valor respecto a un total
 * Retorna 0 si el total es 0 o negativo para evitar divisiones inválidas
 *
 * @param value - Valor numérico del cual calcular el porcentaje
 * @param total - Total sobre el cual calcular el porcentaje
 * @returns Porcentaje con un decimal de precisión (ej: 45.3)
 *
 * @example
 * calculatePercentage(45, 100) // 45.0
 * calculatePercentage(33.333, 100) // 33.3
 * calculatePercentage(10, 0) // 0
 */
export const calculatePercentage = (value: number, total: number): number => {
  return total > 0 ? Number.parseFloat(((value / total) * 100).toFixed(1)) : 0;
};

/**
 * Reduce un array de objetos con propiedades de riesgo a totales agregados
 * Suma las propiedades riesgo_alto, riesgo_medio y riesgo_bajo de todos los items
 *
 * @param data - Array de objetos que contengan propiedades de niveles de riesgo
 * @returns Objeto con los totales agregados por nivel de riesgo
 *
 * @example
 * const data = [
 *   { riesgo_alto: 10, riesgo_medio: 20, riesgo_bajo: 30 },
 *   { riesgo_alto: 5, riesgo_medio: 15, riesgo_bajo: 25 }
 * ];
 * reduceTotalsByRiskLevel(data) // { riesgo_alto: 15, riesgo_medio: 35, riesgo_bajo: 55 }
 */
export const reduceTotalsByRiskLevel = <T extends RiskTotals>(
  data: T[],
): RiskTotals => {
  return data.reduce(
    (acc, item) => ({
      riesgo_alto: acc.riesgo_alto + item.riesgo_alto,
      riesgo_medio: acc.riesgo_medio + item.riesgo_medio,
      riesgo_bajo: acc.riesgo_bajo + item.riesgo_bajo,
    }),
    { riesgo_alto: 0, riesgo_medio: 0, riesgo_bajo: 0 },
  );
};

/**
 * Calcula la suma total de todos los niveles de riesgo
 *
 * @param totals - Objeto con totales por nivel de riesgo
 * @returns Suma de riesgo_alto + riesgo_medio + riesgo_bajo
 *
 * @example
 * sumTotalRisks({ riesgo_alto: 10, riesgo_medio: 20, riesgo_bajo: 30 }) // 60
 */
export const sumTotalRisks = (totals: RiskTotals): number => {
  return totals.riesgo_alto + totals.riesgo_medio + totals.riesgo_bajo;
};

/**
 * Construye un objeto TotalByRiskLevelWithPercentage a partir de totales
 * Calcula automáticamente los porcentajes para cada nivel de riesgo
 * Los porcentajes se calculan sobre la suma total de todos los riesgos
 *
 * @param totals - Objeto con totales por nivel de riesgo
 * @returns Objeto con estructura de total y porcentaje para cada nivel de riesgo
 *
 * @example
 * const totals = { riesgo_alto: 10, riesgo_medio: 20, riesgo_bajo: 70 };
 * buildTotalByRiskLevelWithPercentage(totals)
 * // {
 * //   riesgo_alto: { total: 10, percentage: 10.0 },
 * //   riesgo_medio: { total: 20, percentage: 20.0 },
 * //   riesgo_bajo: { total: 70, percentage: 70.0 }
 * // }
 */
export const buildTotalByRiskLevelWithPercentage = (
  totals: RiskTotals,
): TotalByRiskLevelWithPercentage => {
  const totalRisks = sumTotalRisks(totals);

  return {
    riesgo_alto: {
      total: totals.riesgo_alto,
      percentage: calculatePercentage(totals.riesgo_alto, totalRisks),
    },
    riesgo_medio: {
      total: totals.riesgo_medio,
      percentage: calculatePercentage(totals.riesgo_medio, totalRisks),
    },
    riesgo_bajo: {
      total: totals.riesgo_bajo,
      percentage: calculatePercentage(totals.riesgo_bajo, totalRisks),
    },
  };
};
